import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import MediaBrowser from './MediaBrowser';
import AiRewriteDialog from './AiRewriteDialog';
import RichTextEditorLazy from './RichTextEditorLazy';
const PICKERS = [
    {
        label: 'Picture',
        accept: 'image',
        toToken: (rel, item) => `{{picture:media/${rel}|alt=${escapeAlt(item.alt)}|sizes=hero}}`,
    },
    {
        label: 'Video',
        accept: 'video',
        toToken: (rel) => `{{video:media/${rel}|autoplay=1|loop=1|muted=1}}`,
    },
    {
        label: 'SVG',
        accept: 'svg',
        toToken: (rel) => {
            const base = rel.replace(/^.*\//, '').replace(/\.svg$/i, '');
            return `{{svg:${base}|class=icon}}`;
        },
    },
];
const FORM_TOKEN = '{{form:contact-us}}';
function escapeAlt(s) {
    return s.replace(/[|}]/g, '');
}
export default function ContentEditor({ slug, blockId, blockType = 'text', value, onChange, onSaved, inlineLocked = false, }) {
    const controlled = value !== undefined && onChange !== undefined;
    // Uncontrolled mode keeps the legacy fetch + internal Save flow alive for
    // the AI tab and any caller that hasn't migrated to the draft model yet.
    const [original, setOriginal] = useState('');
    const [internalDraft, setInternalDraft] = useState('');
    const [path, setPath] = useState('');
    const [loading, setLoading] = useState(!controlled);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(null);
    const [aiOpen, setAiOpen] = useState(false);
    const [mode, setMode] = useState('source');
    const taRef = useRef(null);
    const draft = controlled ? value : internalDraft;
    const setDraft = (next) => {
        if (controlled)
            onChange(next);
        else
            setInternalDraft(next);
    };
    useEffect(() => {
        if (controlled) {
            setLoading(false);
            setPath(`content/${slug}/${blockId}.html`);
            return;
        }
        let alive = true;
        setLoading(true);
        setError(null);
        api.getContent(slug, blockId)
            .then(({ html, path }) => { if (!alive)
            return; setOriginal(html); setInternalDraft(html); setPath(path); })
            .catch((e) => { if (alive)
            setError(e?.message ?? 'Failed to load content'); })
            .finally(() => { if (alive)
            setLoading(false); });
        return () => { alive = false; };
    }, [slug, blockId, controlled]);
    async function save() {
        if (controlled)
            return;
        setBusy(true);
        setError(null);
        try {
            await api.putContent(slug, blockId, draft);
            setOriginal(draft);
            onSaved?.();
        }
        catch (e) {
            setError(e?.message ?? 'Save failed');
        }
        finally {
            setBusy(false);
        }
    }
    function insert(token) {
        const ta = taRef.current;
        if (!ta) {
            setDraft(draft + token);
            return;
        }
        const start = ta.selectionStart ?? draft.length;
        const end = ta.selectionEnd ?? draft.length;
        const next = draft.slice(0, start) + token + draft.slice(end);
        setDraft(next);
        requestAnimationFrame(() => {
            ta.focus();
            const pos = start + token.length;
            ta.setSelectionRange(pos, pos);
        });
    }
    function onKeyDown(e) {
        if (controlled)
            return; // parent handles Ctrl+S in draft mode
        if ((e.metaKey || e.ctrlKey) && e.key === 's') {
            e.preventDefault();
            if (draft !== original)
                save();
        }
    }
    const dirty = !controlled && draft !== original;
    return (_jsxs("div", { className: `content-editor${inlineLocked ? ' inline-locked' : ''}`, children: [_jsxs("div", { className: "content-editor-head", children: [_jsx("span", { children: "Content" }), path && _jsx("code", { className: "muted small", children: path }), inlineLocked && _jsx("span", { className: "tag warn-tag", style: { marginLeft: 'auto' }, children: "Locked \u2014 editing inline in canvas" })] }), error && _jsx("div", { className: "err small pad", children: error }), loading ? (_jsx("div", { className: "muted pad", children: "Loading\u2026" })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "token-bar", children: [_jsxs("div", { className: "mode-toggle", role: "tablist", children: [_jsx("button", { type: "button", role: "tab", className: mode === 'source' ? 'active' : '', onClick: () => setMode('source'), title: "Edit raw HTML", children: "\u2328 Source" }), _jsx("button", { type: "button", role: "tab", className: mode === 'rich' ? 'active' : '', onClick: () => setMode('rich'), title: "Visual rich-text edit.", children: "\uD83C\uDFA8 Rich" })] }), _jsx("span", { className: "muted small", children: "Insert:" }), PICKERS.map((p) => (_jsx("button", { type: "button", className: "ghost token-btn", onClick: () => setPickerOpen(p), disabled: mode === 'rich', title: mode === 'rich' ? 'Switch to Source to insert tokens' : '', children: p.label }, p.label))), _jsx("button", { type: "button", className: "ghost token-btn", onClick: () => insert(FORM_TOKEN), disabled: mode === 'rich', children: "Form" }), _jsx("span", { className: "muted small", style: { marginLeft: 'auto' }, children: "AI:" }), _jsx("button", { type: "button", className: "ghost token-btn", onClick: () => setAiOpen(true), disabled: !draft.trim(), title: "Rewrite this block's HTML using AI", children: "\u2728 Rewrite" })] }), mode === 'source' ? (_jsx("textarea", { ref: taRef, className: "content-textarea", value: draft, spellCheck: false, onChange: (e) => setDraft(e.target.value), onKeyDown: onKeyDown, rows: 12 })) : (_jsx(RichTextEditorLazy, { initialHtml: draft, onChange: setDraft, onSave: () => { if (!controlled && draft !== original)
                            save(); } })), !controlled && (_jsxs("div", { className: "content-editor-foot", children: [_jsx("span", { className: "muted small", children: dirty ? 'Unsaved changes' : 'Saved' }), _jsxs("div", { className: "actions", children: [_jsx("button", { className: "ghost", disabled: !dirty || busy, onClick: () => setInternalDraft(original), children: "Reset" }), _jsx("button", { disabled: !dirty || busy, onClick: save, children: busy ? 'Saving…' : 'Save (⌘S)' })] })] })), controlled && (_jsx("div", { className: "content-editor-foot", children: _jsx("span", { className: "muted small", children: "Changes apply on page Save." }) })), pickerOpen && (_jsx(MediaBrowser, { accept: pickerOpen.accept, onClose: () => setPickerOpen(null), onPick: (rel, item) => {
                            insert(pickerOpen.toToken(rel, item));
                            setPickerOpen(null);
                        } })), aiOpen && (_jsx(AiRewriteDialog, { initialHtml: draft, blockType: blockType, onClose: () => setAiOpen(false), onAccept: (html) => { setDraft(html); setAiOpen(false); } }))] }))] }));
}
